import React, { useState, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Row,
  Column,
  Card,
  Typography,
  Link,
  Button,
  Icon,
  FlexBox,
  SkeletonText,
  SkeletonTextLine,
  Box,
} from '@vp/swan';
import { useTranslations, ReplaceTokens } from '@vp/om-translations';
import { formatDate } from '@vp/om-date-formatter';
import { MultiPanePanel } from '@vp/component-library';
import {
  shippingOptionSelectors,
  shippingOptionOperations,
} from '../../state/shippingOptions';
import { addressesSelectors } from '../../state/addresses';
import { cartSelectors } from '../../state/cart';
import type { RootState } from '../../types';
import useSiteData from '../../hooks/useSiteData';
import { ShippingMethodPriceSummary } from './ShippingMethodPriceSummary';
import { ShippingOptionsPanel } from './ShippingOptionsPanel';
import { EmbeddableLinkCreator } from '../shared/EmbeddableLinkCreator';
import { ALERT_DELIVERY_OPTS_ERR_TAG } from '../../constants/analyticMarkers';
import useIdentityContext from '../../hooks/useIdentityContext';
import { trackCustomerRetryShippingFetchAfterFail } from '../../apis/newRelicTracking';
import useTracking from '../../hooks/useTracking';
import { overrideMerchandisingDisplayDataIfNecessary } from '../../util/shippingOptionsHelper';
import { SHIPPING_IN_CART } from '../../optimizely/optimizelyExperiments';

export const SelectedShippingOptionSummary = () => {
  const dispatch = useDispatch();
  const { identity } = useIdentityContext();
  const { shopperId } = identity;
  const {
    locale,
    externalAppNavigation,
    vistacartTenant: tenant,
    siteName,
  } = useSiteData();
  const tracker = useTracking();
  const [shippingMessages] = useTranslations(['shippingMessages']);

  const [isShippingOptionsPanelOpen, setIsShippingOptionsPanelOpen] =
    useState(false);

  const isCartLoaded = useSelector((state: RootState) =>
    cartSelectors.selectIsLoaded(state.cart)
  );

  const hasSelectedShippingOption = useSelector((state: RootState) =>
    shippingOptionSelectors.selectHasSelectedShippingMethod(
      state.shippingOptions
    )
  );

  const selectedShippingMethod = useSelector((state: RootState) =>
    shippingOptionSelectors.selectSelectedShippingMethod(state.shippingOptions)
  );

  const selectedShippingOptionArrivalDate = useSelector((state: RootState) =>
    shippingOptionSelectors.selectSelectedShippingOptionPromisedArrivalDate(
      state.shippingOptions
    )
  );

  const selectedShippingAddressPostalCode = useSelector((state: RootState) =>
    addressesSelectors.selectShippingAddressPostalCode(state.addresses)
  );

  const didCartLoadHitMaxReloadAttempts = useSelector((state: RootState) =>
    cartSelectors.selectDidCartLoadHitMaxReloadAttempts(state.cart)
  );

  const isPendingShippingOperationInProgress = useSelector((state: RootState) =>
    cartSelectors.selectIsPendingShippingOperationInProgress(state.cart)
  );

  const handleOnRequestDismiss = () => {
    setIsShippingOptionsPanelOpen(false);
  };

  const retryFn = useCallback(() => {
    dispatch(shippingOptionOperations.refreshShippingOptions() as any);
    trackCustomerRetryShippingFetchAfterFail(shopperId, tenant, siteName);
  }, [dispatch, shopperId, siteName, tenant]);

  const isShippingOperationInProgress = useSelector((state: RootState) =>
    cartSelectors.selectIsPendingShippingOperationInProgress(state.cart)
  );

  const isRefreshInFlight = useSelector((state: RootState) =>
    shippingOptionSelectors.selectIsRefreshInFlight(state.shippingOptions)
  );

  const displayLoadingState =
    isRefreshInFlight ||
    !isCartLoaded ||
    // if there are shipping option operations in pending ops, and we haven't stopped attempting to load yet
    (isShippingOperationInProgress && !didCartLoadHitMaxReloadAttempts);

  if (displayLoadingState) {
    return (
      <Row>
        <Column span={12} className="selected-shipping-option-container">
          <Card
            bordered
            mt={3}
            data-testid="selected-shipping-option-skeleton-text"
          >
            <SkeletonText>
              <SkeletonTextLine />
            </SkeletonText>
          </Card>
        </Column>
      </Row>
    );
  }

  if (didCartLoadHitMaxReloadAttempts && isPendingShippingOperationInProgress) {
    const ShippingOptionsRetryLink = EmbeddableLinkCreator({
      onClick: retryFn,
    });

    return (
      <Card
        bordered
        className={ALERT_DELIVERY_OPTS_ERR_TAG}
        my={4}
        fontSize={1}
        data-testid="selected-shipping-option-error-state"
      >
        <ReplaceTokens
          tokenizedString={shippingMessages.shippingOptionsErrorMessageRetry}
          substitutions={{
            ShippingOptionsRetryLink,
          }}
        />
      </Card>
    );
  }

  if (
    !displayLoadingState &&
    hasSelectedShippingOption &&
    selectedShippingMethod
  ) {
    const localizedShippingOptionsDisplay =
      overrideMerchandisingDisplayDataIfNecessary(
        selectedShippingMethod,
        locale
      );
    const { name, displayTextOverrides } = localizedShippingOptionsDisplay;
    const selectedShippingOptionName = name;
    const arrivalDateText =
      displayTextOverrides && displayTextOverrides.ARRIVAL_DATE_TEXT ? (
        displayTextOverrides.ARRIVAL_DATE_TEXT
      ) : (
        <ReplaceTokens
          tokenizedString={shippingMessages.arrivalBy}
          substitutions={{
            arrivalDate: formatDate(selectedShippingOptionArrivalDate!, locale),
          }}
        />
      );

    return (
      <>
        <Row>
          <Column span={12} data-testid="selected-shipping-option-container">
            <Card bordered mt={3}>
              <FlexBox
                flexDirection="row"
                flexWrap="wrap"
                justifyContent="space-between"
                fontSize={1}
              >
                <FlexBox
                  flexDirection="row"
                  flexWrap="nowrap"
                  fontWeight="bold"
                  mr={1}
                >
                  <ReplaceTokens
                    tokenizedString={shippingMessages.shippingForPrice}
                    substitutions={{
                      shippingOptionName: (
                        <Box mr={1}>{selectedShippingOptionName}</Box>
                      ),
                      price: <ShippingMethodPriceSummary />,
                    }}
                  />
                </FlexBox>
                <Typography component="span">
                  <ReplaceTokens
                    tokenizedString={shippingMessages.shippingTo}
                    substitutions={{
                      arrivalBy: arrivalDateText,
                      postalCode: (
                        <Link
                          render={p => (
                            <Button
                              skin="link"
                              className={p.className}
                              onClick={e => {
                                e.preventDefault();
                                tracker.trackExperimentClicked(
                                  externalAppNavigation.cart,
                                  externalAppNavigation.cart,
                                  'Change Shipping Address',
                                  SHIPPING_IN_CART.experimentKey,
                                  SHIPPING_IN_CART.activeVariant,
                                  ''
                                );
                                window!.top!.location.href =
                                  externalAppNavigation.checkout;
                              }}
                            >
                              {p.children}
                            </Button>
                          )}
                        >
                          {selectedShippingAddressPostalCode}
                        </Link>
                      ),
                    }}
                  />
                </Typography>
                <Link
                  withIcon
                  render={p => (
                    <Button
                      skin="link"
                      className={p.className}
                      onClick={() => {
                        setIsShippingOptionsPanelOpen(true);
                        tracker.trackExperimentClicked(
                          externalAppNavigation.cart,
                          externalAppNavigation.cart,
                          'moreShippingOptions',
                          SHIPPING_IN_CART.experimentKey,
                          SHIPPING_IN_CART.activeVariant,
                          ''
                        );
                      }}
                    >
                      {p.children}
                    </Button>
                  )}
                >
                  {shippingMessages.moreShippingOptions}
                  <Icon iconType="arrowRight" ml={0} size="16p" />
                </Link>
              </FlexBox>
            </Card>
          </Column>
        </Row>
        <MultiPanePanel
          variant="panel-right"
          isOpen={isShippingOptionsPanelOpen}
          setIsOpen={setIsShippingOptionsPanelOpen}
          onDismissCallback={handleOnRequestDismiss}
          onlyRenderWhenOpen
        >
          <ShippingOptionsPanel setIsOpen={setIsShippingOptionsPanelOpen} />
        </MultiPanePanel>
      </>
    );
  }

  return null;
};
